import { Button } from "@components/atoms/Button"
import { EventGridTile } from "@components/content/EventTile"
import { Input } from "@components/controls/Input"
import { Select } from "@components/controls/Select"
import { TabBar } from "@components/controls/TabBar"
import { Appear } from "@components/motion/Appear"
import { EventGridTileFragment } from "@generated-types"
import { useInfiniteEvents } from "@hooks/queries"
import { useLanguage } from "@hooks/useLanguage"
import { parseDate } from "@utils/dates"
import { getTranslation } from "@utils/Translations"
import { cn } from "@utils/tw"
import { defineBlock } from "eddev/blocks"
import { useRoute } from "eddev/routing"
import { useEffect, useRef, useState } from "react"

export const meta: BlockMeta = {
  title: "Events Grid",
  tags: ["#page"],
}

export default defineBlock("section/events-grid", (props) => {
  const route = useRoute()

  const tabs = [
    { href: "/events", label: getTranslation("all", "All") },
    ...(props.eventsCats?.nodes?.map((n) => n.uri && n.name && { href: n.uri, label: n.name }).filter((n) => !!n) ||
      []),
  ]
  const tabValues = ["all", ...(props.eventsCats?.nodes?.map((n) => n.slug).filter((n) => !!n) || [])]

  const ref = useRef<HTMLDivElement>(null)
  const [tab, setTab] = useState(tabs.findIndex((t) => t.href.includes(route?.pathname.replace("/archive", ""))) || 0)
  const [search, setSearch] = useState("")
  const [category, setCategory] = useState("")
  const [filter, setFilter] = useState("")
  const [sort, setSort] = useState("")
  const [allShown, setAllShown] = useState(false)

  const filters =
    props.eventsTags?.nodes?.map((n) => n.slug && n.name && { value: n.slug, label: n.name }).filter((n) => !!n) || []
  const sorts = [
    { value: "desc", label: getTranslation("newest", "Newest") },
    { value: "asc", label: getTranslation("oldest", "Oldest") },
  ]

  const language = useLanguage()
  const allItemsRef = useRef<EventGridTileFragment[]>([])
  const [allItems, setAllItems] = useState<EventGridTileFragment[]>(props.events?.nodes || [])
  const lookup = useInfiniteEvents(
    { lang: language?.slug },
    {
      refetchOnWindowFocus: false,
    },
  )

  useEffect(() => {
    // fetch all pages
    const fetchAllPages = async () => {
      allItemsRef.current = lookup.data?.nodes || []
      if (allItemsRef.current.length > 0) {
        setAllItems(allItemsRef.current)
      }

      if (lookup.hasNextPage) {
        // console.log("Fetching next page of events...")
        await lookup.fetchNextPage()
      }
    }
    fetchAllPages()
  }, [lookup.data])

  const items =
    allItems
      ?.filter(
        (g) =>
          !!g &&
          (!filter || g.headerData?.tags?.some((t) => t?.slug === filter)) &&
          (!search || g.title?.toLowerCase().includes(search.toLowerCase())) &&
          (tabValues[tab] === "all" || g.category === tabValues[tab]),
      )
      .sort((a, b) => {
        const aTime = parseDate(a.headerData?.eventDateStart || a.date)?.getTime() ?? 0
        const bTime = parseDate(b.headerData?.eventDateStart || b.date)?.getTime() ?? 0
        return sort === "asc" ? aTime - bTime : bTime - aTime
      })
      .slice(0, allShown ? undefined : 9) || []

  return (
    <div className={cn("events-grid my-9 w-maxContentW mx-auto px-6")}>
      <div className="w-full flex flex-col md:flex-row md:justify-between gap-4 mb-6">
        <TabBar
          tabs={tabs}
          activeTab={tab}
          setActiveTab={(t: number) => {
            setTab(t)
            setCategory(tabs[t].label.toLowerCase())
          }}
          className="shrink-0"
        />
        <div className="flex flex-wrap md:flex-nowrap gap-2 items-center justify-center">
          <Input
            placeholder={getTranslation("search", "Search")}
            name="search"
            value={search}
            onChange={(e) => setSearch(e)}
            className="w-full md:w-auto"
          />
          <Select
            placeholder={getTranslation("filter", "Filter")}
            options={filters}
            value={filter}
            onChange={(value) => setFilter(value)}
            className="w-full md:w-auto shrink-0"
          />
          <Select
            placeholder={getTranslation("sortBy", "Sort By")}
            options={sorts}
            value={sort}
            onChange={(value) => setSort(value)}
            className="w-full md:w-auto shrink-0"
          />
        </div>
      </div>
      {items.length === 0 && (
        <div className="w-full my-10 flex items-center justify-center">
          <p className="type-header-s my-0">{getTranslation("noMatchingEvents", "No matching events found.")}</p>
        </div>
      )}
      <div ref={ref} className={cn("grid gap-5 grid-cols-1 md:grid-cols-3")}>
        {items.map((item, ix) => (
          <Appear key={item.id} className="w-full" delay={ix >= 9 ? 0 : ix * 50}>
            <EventGridTile item={item} />
          </Appear>
        ))}
      </div>
      {allItems.length > 9 && !allShown && (
        <div className="w-full flex justify-center mt-6">
          <Button className="type-body-m font-medium text-primary" onClick={() => setAllShown(true)}>
            {getTranslation("showMore", "Show more")}
          </Button>
        </div>
      )}
    </div>
  )
})
